import { Variant } from "@/types/index";
import store from "@/vue/planning/store/store"

/**
 * Validates if two variants have valid skipass durations.
 * @param {Variant} variant1 - The first variant to check.
 * @param {Variant} variant2 - The second variant to check.
 * @returns {boolean} True if both variants have valid skipass durations and are different, false otherwise.
 */
const isValidSkipassDuration = (variant1: Variant, variant2: Variant): boolean => {
  // Different valid durations based on locale
  const validSkipassesDurationNL = [6, 7];
  const validSkipassesDurationFR = [7, 8];
  
  // Check if locale is NL
  if (store.getters.isLocaleNL) {
    return (
      validSkipassesDurationNL.includes(variant1.skipass) &&
      validSkipassesDurationNL.includes(variant2.skipass) &&
      variant1.skipass !== variant2.skipass
    );
  }
  
  // Check if locale is FR
  if (store.getters.isLocaleFR) {
    return (
      validSkipassesDurationFR.includes(variant1.skipass) &&
      validSkipassesDurationFR.includes(variant2.skipass) &&
      variant1.skipass !== variant2.skipass
    );
  }
  
  // Default case - no valid skipass duration
  return false;
};

/**
 * Validates if at least one variant has a valid code package.
 * @param {Variant} variant1 - The first variant to check.
 * @param {Variant} variant2 - The second variant to check.
 * @returns {boolean} True if at least one variant has a valid code package, false otherwise.
 */
const hasValidCodePackage = (variant1: Variant, variant2: Variant): boolean => {
  const validCodePackagesNL: Set<string> = new Set(["PF", "PFP"]);
  const validCodePackagesFR: Set<string> = new Set(["PFTTF", "PFPTTF"]);
  
  if (store.getters.isLocaleNL) {
    return (
      validCodePackagesNL.has(variant1.codePackage) ||
      validCodePackagesNL.has(variant2.codePackage)
    );
  }
  
  if (store.getters.isLocaleFR) {
    return (
      validCodePackagesFR.has(variant1.codePackage) ||
      validCodePackagesFR.has(variant2.codePackage)
    );
  }
  
  return false;
};

/**
 * Determines if the combination of two variants is a valid New SkiPass duration combo 
 * (PF_7 with PF_6 OR PFP_7 with PFP_6 for NL, or PFTTF_8 with PFTTF_7 OR PFPTTF_8 with PFPTTF_7 for FR)
 * and the locale is NL or FR.
 * @param {Variant[]} variants - An array of n variants to check.
 * @returns {boolean} True if the variants form a valid ski pass duration combo and the locale is NL or FR, false otherwise.
 */
export const isValidNewSkiPassPackages = (variants: Variant[]): boolean => {
  if (!variants || variants.length < 2) return false;

  for (let i = 0; i < variants.length; i++) {
    for (let j = i + 1; j < variants.length; j++) {
      // Original NL logic
      if (
        isValidSkipassDuration(variants[i], variants[j]) &&
        hasValidCodePackage(variants[i], variants[j]) &&
        store.getters.isLocaleNL
      ) {
        return true;
      }
      
      // New FR logic
      const frMatch = 
        store.getters.isLocaleFR &&
        ((variants[i].skipass === 7 && variants[j].skipass === 8) || 
         (variants[i].skipass === 8 && variants[j].skipass === 7)) &&
        ((variants[i].codePackage === "PFTTF" && variants[j].codePackage === "PFTTF") ||
         (variants[i].codePackage === "PFPTTF" && variants[j].codePackage === "PFPTTF"));
         
      if (frMatch) {
        return true;
      }
    }
  }

  return false;
};